import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { offerConverter } from '@seu-converter';
import { SEU_NEW_ID } from '@seu-helpers';
import {
  OfferFormService,
  RequestService,
  StoreService,
  ToastService,
} from '@seu-services';
import {
  SEU_API_ENDPOINTS,
  SeuInterconnection,
  SeuOffer,
  SeuOfferApi,
  SeuOperatingCondition,
  SeuOperatingConditionUpdateRequest,
} from '@seu-types';
import { catchError, firstValueFrom, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OperatingConditionService {
  private readonly offerFormService = inject(OfferFormService);
  private readonly store = inject(StoreService);
  private readonly requestService = inject(RequestService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  public getOperatingConditionFromOffer(
    offer: SeuOffer,
    operatingConditionId: string
  ): SeuOperatingCondition | undefined {
    let operatingCondition = undefined;

    offer.interconnections.forEach((interconnection) => {
      interconnection.operatingConditions.forEach((oc) => {
        if (oc.id === operatingConditionId) {
          operatingCondition = oc;
        }
      });
    });

    return operatingCondition;
  }

  public getInterconnectionByOperatingConditionId(
    offer: SeuOffer,
    operatingConditionId: string
  ): SeuInterconnection | undefined {
    let interconnection = undefined;

    offer.interconnections.forEach((ic) => {
      ic.operatingConditions.forEach((oc) => {
        if (oc.id === operatingConditionId) {
          interconnection = ic;
        }
      });
    });
    return interconnection;
  }

  public async createOperatingCondition(
    offerId: string,
    interconnectionId: string,
    data: SeuOperatingConditionUpdateRequest
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.operatingCondition
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId);

    const responseData = await firstValueFrom(
      this.requestService
        .post<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, data)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'operating-condition.toasts.create-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      const interconnectionToUpdate =
        updatedOffer.interconnections.find(
          (item) => item.id === interconnectionId
        ) ?? null;
      this.store.updateActiveInterconnection(interconnectionToUpdate);
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'operating-condition.toasts.create-success',
      // });
    }
  }

  public async updateOperatingCondition(
    offerId: string,
    interconnectionId: string,
    operatingConditionId: string,
    data: SeuOperatingConditionUpdateRequest
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.operatingConditionDetail
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId)
      .replace(':operatingConditionId', operatingConditionId);

    const responseData = await firstValueFrom(
      this.requestService
        .put<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, data)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'operating-condition.toasts.save-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.store.updateActiveInterconnection(
        updatedOffer.interconnections.find(
          (item) => item.id === interconnectionId
        ) ?? null
      );
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'operating-condition.toasts.save-success',
      // });
    }
  }

  public async deleteOperatingCondition(
    offerId: string,
    interconnectionId: string,
    operatingConditionId: string
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.operatingConditionDetail
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId)
      .replace(':operatingConditionId', operatingConditionId);

    const responseData = await firstValueFrom(
      this.requestService
        .delete<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'operating-condition.toasts.delete-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.store.updateActiveInterconnection(
        updatedOffer.interconnections.find(
          (item) => item.id === interconnectionId
        ) ?? null
      );
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'operating-condition.toasts.delete-success',
      // });
    }
  }

  public initNewOperatingCondition(
    offer: SeuOffer,
    interconnection: SeuInterconnection
  ): void {
    const newOperatingCondition: SeuOperatingCondition = {
      id: SEU_NEW_ID,
      title: this.translateService.instant(
        'global.untitled-operating-condition'
      ),
      description: '',
      categories: [],
      inputStreams: [],
      active: false,
    };

    const newInterconnection = {
      ...interconnection,
      operatingConditions: [
        ...interconnection.operatingConditions,
        newOperatingCondition,
      ],
    };

    const interconnections = offer.interconnections.map((item) =>
      item.id === interconnection.id ? newInterconnection : item
    );

    this.store.setActiveOffer({
      ...offer,
      interconnections,
    });

    this.offerFormService.init({
      ...offer,
      interconnections,
    });
  }

  public discardNewOperatingCondition(
    offer: SeuOffer,
    interconnectionId: string
  ): void {
    const cleanedInterconnections = offer.interconnections.map((item) => {
      if (item.id !== interconnectionId) {
        return item;
      }

      return {
        ...item,
        operatingConditions: item.operatingConditions.filter(
          (oc) => oc.id !== SEU_NEW_ID
        ),
      };
    });

    const cleanedOffer: SeuOffer = {
      ...offer,
      interconnections: cleanedInterconnections,
    };

    const cleanedInterconnection =
      cleanedInterconnections.find((item) => item.id === interconnectionId) ??
      null;
    this.store.setActiveOffer(cleanedOffer);
    this.store.updateActiveInterconnection(cleanedInterconnection);
    this.offerFormService.init(cleanedOffer);
  }

  public async activateOperatingCondition(
    offerId: string,
    interconnectionId: string,
    operatingConditionId: string
  ): Promise<void> {
    if (this.store.isReadonlyMode()) {
      // If the offer is in readonly mode, we only update the stored offer and do not make an API call
      const offer = await firstValueFrom(this.store.activeOffer$);
      if (!offer) {
        return;
      }
      const updatedOffer: SeuOffer = {
        ...offer,
        interconnections:
          offer?.interconnections.map((ic) => ({
            ...ic,
            operatingConditions: ic.operatingConditions.map((oc) => ({
              ...oc,
              active: oc.id === operatingConditionId,
            })),
          })) ?? [],
      };
      this.store.setActiveOffer(updatedOffer);
      return;
    }

    const endpoint = SEU_API_ENDPOINTS.operatingConditionActivate
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId)
      .replace(':operatingConditionId', operatingConditionId);

    const responseData = await firstValueFrom(
      this.requestService
        .put<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, {})
        .pipe(catchError(() => of(null)))
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
    }
  }

  public async duplicateOperatingCondition(
    offerId: string,
    interconnectionId: string,
    operatingConditionId: string,
    operatingConditionTitle: string
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.operatingConditionDuplicate
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId)
      .replace(':operatingConditionId', operatingConditionId)
      .concat(`?title=${encodeURIComponent(operatingConditionTitle)}`);

    const responseData = await firstValueFrom(
      this.requestService
        .post<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, null)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'operating-condition.toasts.duplicate-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'operating-condition.toasts.duplicate-success',
      // });
    }
  }
}
